/**
 * Refresh + retry orchestrator.
 *
 * Wraps apiClient so that a 401 on the original request triggers a single
 * refresh attempt, then retries the original request once. Concurrent
 * 401s share the same refresh promise (single-flight).
 */

import { ofetch, type FetchOptions } from "ofetch";

import {
  apiClient,
  ApiClientError,
  getAccessToken,
  readPersistedRefresh,
  setAccessToken,
  writePersistedRefresh,
} from "./client";
import { endpoints } from "./endpoints";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";
const AUTH_FREE_PATHS = new Set<string>([endpoints.auth.login, endpoints.auth.register]);

let inflightRefresh: Promise<string | null> | null = null;

async function refreshOnce(): Promise<string | null> {
  const refreshToken = readPersistedRefresh();
  if (!refreshToken) return null;
  try {
    const tokens = await ofetch<{ access_token: string; refresh_token: string }>(
      endpoints.auth.refresh,
      {
        baseURL,
        method: "POST",
        body: { refresh_token: refreshToken },
        retry: 0,
      },
    );
    setAccessToken(tokens.access_token);
    writePersistedRefresh(tokens.refresh_token);
    return tokens.access_token;
  } catch {
    setAccessToken(null);
    writePersistedRefresh(null);
    return null;
  }
}

async function acquireRefreshedAccessToken(): Promise<string | null> {
  if (!inflightRefresh) {
    inflightRefresh = refreshOnce().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

/**
 * Run an API call. If it returns 401, try one silent refresh + retry.
 * If refresh fails, the original 401 surfaces and the caller handles it.
 */
export async function callWithRefresh<T>(
  path: string,
  options?: FetchOptions<"json">,
): Promise<T> {
  try {
    return await apiClient<T>(path, options);
  } catch (err) {
    if (!(err instanceof ApiClientError) || err.status !== 401) {
      throw err;
    }
    if (AUTH_FREE_PATHS.has(path)) {
      throw err;
    }
    const newToken = await acquireRefreshedAccessToken();
    if (!newToken) {
      throw err;
    }
    // Retry once with the new token. Reuse the same path + options.
    const retryOptions: FetchOptions<"json"> = {
      ...(options ?? {}),
      headers: {
        ...(options?.headers as Record<string, string> | undefined),
        Authorization: `Bearer ${newToken}`,
      },
    };
    return await apiClient<T>(path, retryOptions);
  }
}

export function isAuthed(): boolean {
  return getAccessToken() !== null;
}
